import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";

// Actions
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
} from "constants/actions";

// Pulse Survey Context
import { usePulseSurveys, PulseSurveysProvider } from "app/audit/AI/PulseSurvey_V2/Data/LandingPageContext";

// Schedule Context for non-pulse surveys
import { useScheduleContext, ScheduleProvider } from "./ScheduleContext";
import ScheduleBreadcrumb from "./ScheduleBreadcrumb";

// Icons (simplified inline SVG components)
const CalendarIcon = () => (
  <svg
      width="15"
      height="15"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1_158)">
        <path
          d="M3.02222 20H16.9778C18.4407 20 19.6296 18.8111 19.6296 17.3481V4.13331C19.6296 2.67035 18.4407 1.48145 16.9778 1.48145H15.9259V0.740727C15.9259 0.333315 15.5926 0 15.1851 0C14.7777 0 14.4444 0.333315 14.4444 0.740727V1.48145H5.55557V0.740727C5.55557 0.333315 5.2222 0 4.81479 0C4.40737 0 4.07406 0.333315 4.07406 0.740727V1.48145H3.02222C1.55926 1.48145 0.370361 2.67035 0.370361 4.13331V17.3481C0.370361 18.8111 1.55926 20 3.02222 20ZM1.85188 4.13331C1.85188 3.48889 2.3778 2.96297 3.02222 2.96297H4.07406V3.7037C4.07406 4.11111 4.40737 4.44442 4.81479 4.44442C5.2222 4.44442 5.55551 4.11111 5.55551 3.7037V2.96297H14.4444V3.7037C14.4444 4.11111 14.7777 4.44442 15.1851 4.44442C15.5926 4.44442 15.9259 4.11111 15.9259 3.7037V2.96297H16.9777C17.6221 2.96297 18.1481 3.48889 18.1481 4.13331V17.3481C18.1481 17.9926 17.6221 18.5185 16.9777 18.5185H3.02222C2.3778 18.5185 1.85188 17.9926 1.85188 17.3481V4.13331Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 8.51847C6.25222 8.51847 6.66676 8.10393 6.66676 7.59256C6.66676 7.0812 6.25222 6.66666 5.74085 6.66666C5.22948 6.66666 4.81494 7.0812 4.81494 7.59256C4.81494 8.10393 5.22948 8.51847 5.74085 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 8.51847C10.3262 8.51847 10.7407 8.10393 10.7407 7.59256C10.7407 7.0812 10.3262 6.66666 9.81482 6.66666C9.30346 6.66666 8.88892 7.0812 8.88892 7.59256C8.88892 8.10393 9.30346 8.51847 9.81482 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 8.51847C14.4002 8.51847 14.8147 8.10393 14.8147 7.59256C14.8147 7.0812 14.4002 6.66666 13.8888 6.66666C13.3774 6.66666 12.9629 7.0812 12.9629 7.59256C12.9629 8.10393 13.3774 8.51847 13.8888 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 12.2222C6.25222 12.2222 6.66676 11.8076 6.66676 11.2963C6.66676 10.7849 6.25222 10.3704 5.74085 10.3704C5.22948 10.3704 4.81494 10.7849 4.81494 11.2963C4.81494 11.8076 5.22948 12.2222 5.74085 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 12.2222C10.3262 12.2222 10.7407 11.8076 10.7407 11.2963C10.7407 10.7849 10.3262 10.3704 9.81482 10.3704C9.30346 10.3704 8.88892 10.7849 8.88892 11.2963C8.88892 11.8076 9.30346 12.2222 9.81482 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 12.2222C14.4002 12.2222 14.8147 11.8076 14.8147 11.2963C14.8147 10.7849 14.4002 10.3704 13.8888 10.3704C13.3774 10.3704 12.9629 10.7849 12.9629 11.2963C12.9629 11.8076 13.3774 12.2222 13.8888 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 15.9259C6.25222 15.9259 6.66676 15.5113 6.66676 15C6.66676 14.4886 6.25222 14.0741 5.74085 14.0741C5.22948 14.0741 4.81494 14.4886 4.81494 15C4.81494 15.5113 5.22948 15.9259 5.74085 15.9259Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 15.9259C10.3262 15.9259 10.7407 15.5113 10.7407 15C10.7407 14.4886 10.3262 14.0741 9.81482 14.0741C9.30346 14.0741 8.88892 14.4886 8.88892 15C8.88892 15.5113 9.30346 15.9259 9.81482 15.9259Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 15.9259C14.4002 15.9259 14.8147 15.5113 14.8147 15C14.8147 14.4886 14.4002 14.0741 13.8888 14.0741C13.3774 14.0741 12.9629 14.4886 12.9629 15C12.9629 15.5113 13.3774 15.9259 13.8888 15.9259Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_1_158">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.7L16.2,16.2Z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const EngagementIcon = () => (
  <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 9.5V6.8C20 5.11984 20 4.27976 19.673 3.63803C19.3854 3.07354 18.9265 2.6146 18.362 2.32698C17.7202 2 16.8802 2 15.2 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H14M14 11H8M10 15H8M16 7H8M16.5 15.0022C16.6762 14.5014 17.024 14.079 17.4817 13.81C17.9395 13.5409 18.4777 13.4426 19.001 13.5324C19.5243 13.6221 19.999 13.8942 20.3409 14.3004C20.6829 14.7066 20.87 15.2207 20.8692 15.7517C20.8692 17.2506 18.6209 18 18.6209 18M18.6499 21H18.6599" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
);

const PulseIcon = () => (
  <svg width="50%" height="50%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);

// Additional icons for second-step pulse options (outlined)
const TemplateIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2.26953V6.40007C14 6.96012 14 7.24015 14.109 7.45406C14.2049 7.64222 14.3578 7.7952 14.546 7.89108C14.7599 8.00007 15.0399 8.00007 15.6 8.00007H19.7305M16 13H8M16 17H8M10 9H8M14 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H15.2C16.8802 22 17.7202 22 18.362 21.673C18.9265 21.3854 19.3854 20.9265 19.673 20.362C20 19.7202 20 18.8802 20 17.2V8L14 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BuildIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 2.26953V6.40007C14 6.96012 14 7.24015 14.109 7.45406C14.2049 7.64222 14.3578 7.7952 14.546 7.89108C14.7599 8.00007 15.0399 8.00007 15.6 8.00007H19.7305M12 18V12M9 15H15M14 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H15.2C16.8802 22 17.7202 22 18.362 21.673C18.9265 21.3854 19.3854 20.9265 19.673 20.362C20 19.7202 20 18.8802 20 17.2V8L14 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Mock data - replace with actual data from Redux/API
// Temporarily commented out - will be replaced with real pulse survey data
// const mockActiveSurveys = [
//   {
//     id: 1,
//     title: "Q4 Employee Engagement Survey",
//     type: "Culture Audit",
//     status: "active",
//     startDate: "2024-01-15",
//     endDate: "2024-01-29",
//     participants: 156,
//     responses: 89
//   },
//   {
//     id: 2,
//     title: "Leadership Effectiveness Review",
//     type: "360 Review",
//     status: "scheduled",
//     startDate: "2024-02-01",
//     endDate: "2024-02-15",
//     participants: 24,
//     responses: 0
//   },
//   {
//     id: 3,
//     title: "DEI Pulse Check",
//     type: "EDI Survey",
//     status: "active",
//     startDate: "2024-01-20",
//     endDate: "2024-02-05",
//     participants: 203,
//     responses: 145
//   }
// ];

// const mockHistoricalSurveys = [
//   {
//     id: 4,
//     title: "Q3 Team Dynamics Assessment",
//     type: "Culture Audit",
//     status: "completed",
//     startDate: "2023-12-01",
//     endDate: "2023-12-15",
//     participants: 178,
//     responses: 156,
//     completionRate: "87.6%"
//   },
//   {
//     id: 5,
//     title: "Annual Culture Survey 2023",
//     type: "Culture Audit",
//     status: "completed",
//     startDate: "2023-10-15",
//     endDate: "2023-11-15",
//     participants: 245,
//     responses: 201,
//     completionRate: "82.0%"
//   },
//   {
//     id: 6,
//     title: "Manager Feedback Survey",
//     type: "360 Review",
//     status: "completed",
//     startDate: "2023-11-01",
//     endDate: "2023-11-10",
//     participants: 45,
//     responses: 42,
//     completionRate: "93.3%"
//   }
// ];

// Helpers to safely read numeric counts from mixed sources (schedule vs pulse)
const toNum = (val, fallback = 0) => {
  const n = typeof val === 'string' ? Number(val) : val;
  return Number.isFinite(n) ? n : fallback;
};

const getCounts = (survey) => {
  // For non-pulse surveys, use the authoritative survey data
  const participants = toNum(
    survey?.participants ?? survey?.rawData?.total_requests ?? 0,
    0
  );
  const responses = toNum(
    survey?.responses ?? survey?.rawData?.total_responses ?? 0,
    0
  );
  return { participants, responses };
};

const SurveySchedulingContent = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [activeTab, setActiveTab] = useState("active");
  const [showSurveyTypeModal, setShowSurveyTypeModal] = useState(false);
  const [scheduleModalStep, setScheduleModalStep] = useState("type"); // 'type' | 'pulse'
  const dispatch = useDispatch();

  // Get current user info
  const { get_auth } = useSelector(
    (state) => ({
      get_auth: state.auth,
    }),
    shallowEqual
  );

  // Get pulse survey data from context - all pre-filtered by provider
  const { templatesData, draftSurveys, activeSurveys, completedSurveys, rawTemplatesData } =
    usePulseSurveys();

  // Get non-pulse survey data from schedule context
  const {
    getCurrentSurveys,
    formatSurveyData,
    fetchActiveSurveys,
    fetchHistoricalSurveys,
    loading: scheduleLoading,
  } = useScheduleContext();

  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });

    // Fetch non-pulse survey data on component mount
    fetchActiveSurveys();
  }, [dispatch, fetchActiveSurveys]);

  // Fetch appropriate data when tab changes
  useEffect(() => {
    if (activeTab === "historical") {
      fetchHistoricalSurveys();
    } else {
      fetchActiveSurveys();
    }
  }, [activeTab, fetchActiveSurveys, fetchHistoricalSurveys]);

  // Helper function to transform survey data to ScheduleV2 format
  const transformSurveyData = (surveys, defaultStatus) => {
    return (
      surveys?.map((survey) => {
        // Use the default status passed in - no filtering logic here
        const status = defaultStatus;

        return {
          id: survey.id,
          name: typeof survey.name === "string" ? survey.name.trim() : "",
          title: survey.name,
          type: "Pulse Survey",
          status: status,
          startDate: null, // Pulse surveys don't have scheduled dates
          endDate: null,
          participants: survey.participants || 0, // Use the participants count already calculated by LandingPageContext
          responses: 0, // Will need actual response count
          completionRate: status === "completed" ? "100%" : null,
          updatedAt: survey.updatedValue,
        };
      }) || []
    );
  };

  // Get non-pulse survey data
  const currentNonPulseSurveys = getCurrentSurveys();
  const formattedNonPulseSurveys = formatSurveyData(currentNonPulseSurveys);

  // Transform the pre-filtered pulse data from context - NO FILTERING, just transform
  // const transformedTemplates = transformSurveyData(templatesData, "scheduled");
  const transformedPulseDrafts = transformSurveyData(draftSurveys, "draft");
  const transformedPulseActive = transformSurveyData(activeSurveys, "active");
  const transformedPulseCompleted = transformSurveyData(
    completedSurveys,
    "completed"
  );

  // Split non-pulse surveys by status
  const activeNonPulseSurveys = formattedNonPulseSurveys.filter((survey) => {
    const status = (survey.status || "").toLowerCase();
    return ["pending", "not started", "live", "processing", "demo"].includes(
      status
    );
  });


  const completedNonPulseSurveys = formattedNonPulseSurveys.filter((survey) => {
    const status = (survey.status || "").toLowerCase();
    if (!status) return false;

    // Support composite labels like "completed / closed" sent by the API.
    if (status.includes("completed") || status.includes("closed")) {
      return true;
    }

    const statusCode = Number(survey?.rawData?.status);
    return [5, 6].includes(statusCode); // defensive fallback for completed/closed numeric codes
  });

  console.log(draftSurveys, activeSurveys, activeNonPulseSurveys, completedNonPulseSurveys)

  // Combine pulse and non-pulse surveys for display
  const activeDisplaySurveys = [
    ...transformedPulseDrafts,
    ...transformedPulseActive,
    ...activeNonPulseSurveys,
  ];
  const historicalDisplaySurveys = [
    ...transformedPulseCompleted,
    ...completedNonPulseSurveys,
  ];

  // Handle scheduling new survey - show modal for survey type selection
  const handleScheduleSurvey = () => {
    setScheduleModalStep("type");
    setShowSurveyTypeModal(true);
  };

  // Handle open survey library
  const handleOpenLibrary = () => {
    navigate("/leader/audit/schedule-v2/pulse/survey-library", {
      state: {
        fromSchedule: true,
        returnTo: "/leader/audit/schedule-v2",
      },
    });
  };

  // Handle survey type selection
  const handleSurveyTypeSelect = (surveyType) => {
    if (surveyType === "engagement") {
      setShowSurveyTypeModal(false);
      // Navigate to engagement survey scheduling
      navigate("/leader/audit/schedule-v2/new");
    } else if (surveyType === "pulse") {
      // Transition modal to pulse options instead of immediate navigation
      setScheduleModalStep("pulse");
    }
  };

  // Close survey type modal
  const closeSurveyTypeModal = () => {
    setShowSurveyTypeModal(false);
  };

  // Handle second-step selection for Pulse
  const handlePulseCreateSelect = (option) => {
    // option: 'template' | 'build'
    setShowSurveyTypeModal(false);
    if (option === 'template') {
      handleOpenLibrary();
    } else if (option === 'build') {
      navigate("/leader/audit/schedule-v2/pulse/create", {
        state: { returnTo: "/leader/audit/schedule-v2", fromSchedule: true },
      });
    }
  };

  // Helper function to check if user can edit a survey
  const canUserEditSurvey = (survey) => {
    if (!get_auth?.employee_id) return false;

    const userId = Number(get_auth.employee_id);

    // Find the original survey data from rawTemplatesData
    const originalSurvey = rawTemplatesData?.find((s) => s.id === survey.id);
    if (!originalSurvey) return false;

    // Check if user is creator
    const isCreator = originalSurvey?.user_tasks?.find(
      (task) =>
        task && task.task_type === "created_by" && task.user_id === userId
    );

    // Check if user is collaborator
    const collaboratorsTask = originalSurvey?.user_tasks?.find(
      (task) => task && task.task_type === "collaborators"
    );
    const isCollaborator = collaboratorsTask?.collaborators?.includes(userId);

    return Boolean(isCreator || isCollaborator);
  };

  // Handle survey row click
  const handleSurveyClick = (survey) => {
    if (survey.type === "Pulse Survey") {
      if (survey.status === "draft") {
        // Navigate to edit draft pulse survey, include return path
        navigate(`/leader/audit/schedule-v2/pulse/edit/${survey.id}`, {
          state: { returnTo: "/leader/audit/schedule-v2", from: "schedule" },
        });
      } else if (survey.status === "active") {
        // Navigate to view active pulse survey
        navigate(`/leader/audit/schedule-v2/pulse/manage/${survey.id}`);
      } else if (survey.status === "completed") {
        // Navigate to view completed pulse survey results
        navigate(`/leader/audit/schedule-v2/pulse/view/${survey.id}`);
      } else {
        // Scheduled pulse survey - navigate to edit, include return path
        navigate(`/leader/audit/schedule-v2/pulse/edit/${survey.id}`, {
          state: { returnTo: "/leader/audit/schedule-v2", from: "schedule" },
        });
      }
    } else if (survey.type === "Culture Audit") {
      // Handle Culture Audit surveys (non-pulse)
      const statusText = (survey.status || "").toLowerCase().trim();

      if (
        ["pending", "not started", "live", "processing", "demo"].includes(
          statusText
        )
      ) {
        // Navigate to manage culture audit survey (including not started surveys)
        navigate(`/leader/audit/schedule-v2/manage/${survey.id}`);
      } else if (
        statusText.includes("completed") || statusText.includes("closed")
      ) {
        // Navigate to view completed culture audit survey results
        navigate(`/leader/audit/schedule-v2/results/${survey.id}`);
      } else {
        // Default fallback for other statuses
        console.log("Culture audit survey clicked:", survey);
      }
    } else {
      // Handle other survey types (engagement, 360, etc.)
      if (survey.status === "active") {
        // Navigate to manage active survey
        navigate(`/leader/audit/schedule-v2/manage/${survey.id}`);
      } else {
        // Navigate to survey results or details
        console.log("Survey clicked:", survey);
        // Example: navigate(`/leader/audit/surveys/${survey.id}/results`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getSurveyDisplayName = (survey) => {
    if (!survey) return "";

    const named = typeof survey.name === "string" ? survey.name.trim() : "";
    if (named) return named;

    const structured =
      typeof survey.surveyStructureName === "string"
        ? survey.surveyStructureName
        : "";
    if (structured) return structured;

    return survey.title;
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <StatusBadge status="active">
            <CheckIcon /> Live
          </StatusBadge>
        );
      case "live":
        return (
          <StatusBadge status="active">
            <CheckIcon /> Live
          </StatusBadge>
        );
      case "scheduled":
        return (
          <StatusBadge status="scheduled">
            <ClockIcon />{" "}
            {"Scheduled".charAt(0).toUpperCase() + "Scheduled".slice(1)}
          </StatusBadge>
        );
      case "not started":
        return (
          <StatusBadge status="scheduled">
            <ClockIcon /> Not Started
          </StatusBadge>
        );
      case "draft":
        return (
          <StatusBadge status="draft">
            <ClockIcon /> {"Draft".charAt(0).toUpperCase() + "Draft".slice(1)}
          </StatusBadge>
        );
      case "completed":
        return (
          <StatusBadge status="completed">
            <CheckIcon />{" "}
            {"Completed".charAt(0).toUpperCase() + "Completed".slice(1)}
          </StatusBadge>
        );
      case "demo":
        return (
          <StatusBadge status="demo">
            <CheckIcon /> {"Demo".charAt(0).toUpperCase() + "Demo".slice(1)}
          </StatusBadge>
        );
      default:
        return (
          <StatusBadge status="default">
            {status && typeof status === "string"
              ? status.charAt(0).toUpperCase() + status.slice(1)
              : status}
          </StatusBadge>
        );
    }
  };


  console.log();
  return (
    <Container>
      <ScheduleBreadcrumb />
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>Survey Admin</Title>
            <Subtitle>Build, schedule, and manage all surveys</Subtitle>
          </HeaderText>
        </HeaderContent>

        <ButtonGroup>
          <SurveyLibraryButton onClick={handleOpenLibrary}>
            Survey Library
          </SurveyLibraryButton>

          <ScheduleButton onClick={handleScheduleSurvey}>
            <ButtonIcon>
              <PlusIcon />
            </ButtonIcon>
            <ButtonText>Schedule Survey</ButtonText>
          </ScheduleButton>
        </ButtonGroup>
      </HeaderSection>

      {/* Tab Navigation */}
      <TabSection>
        <TabNavigation>
          <TabButton
            active={activeTab === "active"}
            onClick={() => setActiveTab("active")}
          >
            Active Surveys ({activeDisplaySurveys.length})
          </TabButton>
          <TabButton
            active={activeTab === "historical"}
            onClick={() => setActiveTab("historical")}
          >
            Historical Surveys ({historicalDisplaySurveys.length})
          </TabButton>
        </TabNavigation>
      </TabSection>

      {/* Content Section */}
      <ContentSection>
        {activeTab === "active" ? (
          <TableSection>
            {/* <TableHint>
              💡 Click on any survey to view details and manage settings
            </TableHint> */}
            {activeDisplaySurveys.length > 0 ? (
              <SurveysTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Survey Name</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Last Updated</TableHeaderCell>
                    <TableHeaderCell>Progress</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {activeDisplaySurveys.map((survey, index) => (
                    <ClickableTableRow
                      key={survey.id}
                      index={index}
                      onClick={() => handleSurveyClick(survey)}
                      title="Click to view survey details"
                    >
                      <TableCell>
                        <SurveyInfo>
                          <SurveyTitle>
                            {getSurveyDisplayName(survey)}
                          </SurveyTitle>
                          {/* <SurveySubtitle>{!survey.surveyStructureName && survey.title}</SurveySubtitle> */}
                        </SurveyInfo>
                      </TableCell>
                      <TableCell>
                        <SurveyType>{survey.type}</SurveyType>
                      </TableCell>
                      <TableCell>{getStatusBadge(survey.status)}</TableCell>
                      <TableCell>
                        <DateRange>
                          {survey.updatedAt && (
                            <DateText>
                              Updated: {formatDate(survey.updatedAt)}
                            </DateText>
                          )}
                        </DateRange>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (survey.status === "draft") {
                            return (
                              <ProgressInfo>
                                <ProgressText>Draft - Not sent</ProgressText>
                                <ProgressBar>
                                  <ProgressFill percentage={0} />
                                </ProgressBar>
                              </ProgressInfo>
                            );
                          }

                          // For Pulse Surveys, only show participant count (not progress)
                          if (survey.type === "Pulse Survey") {
                            const { participants } = getCounts(survey);
                            return (
                              <ProgressInfo>
                                <ProgressText>
                                  {participants}{" "}
                                  {participants === 1
                                    ? "participant"
                                    : "participants"}
                                </ProgressText>
                              </ProgressInfo>
                            );
                          }

                          // For non-pulse surveys that haven't started, show "Not Started"
                          if (survey.type !== "Pulse Survey" && survey.status === "not started") {
                            return (
                              <ProgressInfo>
                                <ProgressText>Not Started</ProgressText>
                              </ProgressInfo>
                            );
                          }

                          // For non-pulse surveys, show the full progress bar
                          const { participants, responses } = getCounts(survey);
                          const pct =
                            participants > 0
                              ? (responses / participants) * 100
                              : 0;

                          return (
                            <ProgressInfo>
                              <ProgressText>
                                {responses} / {participants}{" "}
                                responses
                              </ProgressText>
                              <ProgressBar>
                                <ProgressFill percentage={pct} />
                              </ProgressBar>
                            </ProgressInfo>
                          );
                        })()}
                      </TableCell>
                    </ClickableTableRow>
                  ))}
                </TableBody>
              </SurveysTable>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <CalendarIcon />
                </EmptyIcon>
                <EmptyTitle>No Active Surveys</EmptyTitle>
                <EmptyText>
                  Schedule your first survey to start collecting valuable
                  feedback from your team.
                </EmptyText>
                <EmptyButton onClick={handleScheduleSurvey}>
                  <PlusIcon />
                  Schedule First Survey
                </EmptyButton>
              </EmptyState>
            )}
          </TableSection>
        ) : (
          <TableSection>
            {/* <TableHint>📊 View completed surveys and their results</TableHint> */}
            {historicalDisplaySurveys.length > 0 ? (
              <SurveysTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Survey Name</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Last Updated</TableHeaderCell>
                    <TableHeaderCell>Completion Rate</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {historicalDisplaySurveys.map((survey, index) => (
                    <ClickableTableRow
                      key={survey.id}
                      index={index}
                      onClick={() => handleSurveyClick(survey)}
                      title="Click to view survey results"
                    >
                      <TableCell>
                        <SurveyInfo>
                          <SurveyTitle>
                            {getSurveyDisplayName(survey)}
                          </SurveyTitle>
                          <SurveySubtitle>{survey.title}</SurveySubtitle>
                        </SurveyInfo>
                      </TableCell>
                      <TableCell>
                        <SurveyType>{survey.type}</SurveyType>
                      </TableCell>
                      <TableCell>{getStatusBadge(survey.status)}</TableCell>
                      <TableCell>
                        <DateRange>
                          {survey.updatedAt && (
                            <DateText>
                              Updated: {formatDate(survey.updatedAt)}
                            </DateText>
                          )}
                        </DateRange>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // For Pulse Surveys, only show participant count (not completion rate)
                          if (survey.type === "Pulse Survey") {
                            const { participants } = getCounts(survey);
                            return (
                              <CompletionInfo>
                                <CompletionText>
                                  {participants}{" "}
                                  {participants === 1
                                    ? "participant"
                                    : "participants"}
                                </CompletionText>
                              </CompletionInfo>
                            );
                          }

                          // For non-pulse surveys, show completion rate
                          const { participants, responses } = getCounts(survey);
                          const computedRate =
                            participants > 0
                              ? `${Math.round(
                                  (responses / participants) * 100
                                )}%`
                              : "0%";
                          return (
                            <CompletionInfo>
                              <CompletionRate>
                                {survey.completionRate || computedRate}
                              </CompletionRate>
                              <CompletionText>
                                {responses} / {participants} responses
                              </CompletionText>
                            </CompletionInfo>
                          );
                        })()}
                      </TableCell>
                    </ClickableTableRow>
                  ))}
                </TableBody>
              </SurveysTable>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <CalendarIcon />
                </EmptyIcon>
                <EmptyTitle>No Historical Surveys</EmptyTitle>
                <EmptyText>
                  Completed surveys will appear here once you've finished
                  running them.
                </EmptyText>
              </EmptyState>
            )}
          </TableSection>
        )}
      </ContentSection>

      {/* Survey Type Selection Modal */}
      {showSurveyTypeModal && (
        <ModalOverlay onClick={closeSurveyTypeModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {scheduleModalStep === 'type' ? 'Choose Survey Type' : 'Create New Pulse Survey'}
              </ModalTitle>
              <CloseButton onClick={closeSurveyTypeModal}>
                <CloseIcon />
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              {scheduleModalStep === 'type' ? (
                <>
                  <ModalSubtitle>
                    Select the type of survey you'd like to create
                  </ModalSubtitle>

                  <SurveyTypeOptions>
                    <SurveyTypeOption
                      onClick={() => handleSurveyTypeSelect("engagement")}
                    >
                      <SurveyTypeIconWrapper primary>
                        <EngagementIcon />
                      </SurveyTypeIconWrapper>
                      <SurveyTypeContent>
                        <SurveyTypeTitle>Engagement Survey</SurveyTypeTitle>
                        <SurveyTypeDescription>
                          Comprehensive survey for in-depth contact feedback and
                          culture assessment
                        </SurveyTypeDescription>
                      </SurveyTypeContent>
                      <SurveyTypeArrow>
                        <ArrowRightIcon />
                      </SurveyTypeArrow>
                    </SurveyTypeOption>

                    <SurveyTypeOption
                      onClick={() => handleSurveyTypeSelect("pulse")}
                    >
                      <SurveyTypeIconWrapper secondary>
                        <PulseIcon />
                      </SurveyTypeIconWrapper>
                      <SurveyTypeContent>
                        <SurveyTypeTitle>Pulse Survey</SurveyTypeTitle>
                        <SurveyTypeDescription>
                          Quick, focused survey for rapid feedback and real-time
                          insights
                        </SurveyTypeDescription>
                      </SurveyTypeContent>
                      <SurveyTypeArrow>
                        <ArrowRightIcon />
                      </SurveyTypeArrow>
                    </SurveyTypeOption>
                  </SurveyTypeOptions>
                </>
              ) : (
                <>
                  <ModalSubtitle>
                    Choose how you'd like to create your pulse survey
                  </ModalSubtitle>
                  <SecondStepOptionsContainer>
                    <SecondStepOptionCard onClick={() => handlePulseCreateSelect('template')}>
                      <SecondStepIconContainer>
                        <TemplateIcon />
                      </SecondStepIconContainer>
                      <SecondStepOptionTitle>Choose Template</SecondStepOptionTitle>
                      <SecondStepOptionDescription>
                        Browse your library and start from a ready-made pulse template
                      </SecondStepOptionDescription>
                    </SecondStepOptionCard>

                    <SecondStepOptionCard onClick={() => handlePulseCreateSelect('build')}>
                      <SecondStepIconContainer>
                        <BuildIcon />
                      </SecondStepIconContainer>
                      <SecondStepOptionTitle>Build New Template</SecondStepOptionTitle>
                      <SecondStepOptionDescription>
                        Start fresh and build a new pulse survey from scratch
                      </SecondStepOptionDescription>
                    </SecondStepOptionCard>
                  </SecondStepOptionsContainer>
                </>
              )}

            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Wrap the main component with both providers
const SurveyScheduling = () => (
  <ScheduleProvider>
    <PulseSurveysProvider>
      <SurveySchedulingContent />
    </PulseSurveysProvider>
  </ScheduleProvider>
);

export default SurveyScheduling;

// Styled Components - Scaled down from the reference
const Container = styled.div`
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 28px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SurveyLibraryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #476dfa;
  border-radius: 7px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #476dfa;

  &:hover {
    background: #e9efff;
    border-color: #3d5ce6;
  }
`;

const ScheduleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: white;
`;

const TabSection = styled.div`
  margin-bottom: 20px;
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  background: ${(props) => (props.active ? "#476dfa" : "transparent")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.active ? "white" : "#7e7e7e")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.active ? "#3d5ce6" : "#e9ecef")};
  }
`;

const ContentSection = styled.div``;

const TableSection = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHint = styled.div`
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-family: "Poppins";
  font-size: 12px;
  color: #7e7e7e;
  text-align: center;
`;

const SurveysTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const ClickableTableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s;
  animation: slideInRow 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f0f4ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(71, 109, 250, 0.08);
  }

  @keyframes slideInRow {
    from {
      opacity: 0;
      transform: translateX(-16px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${(props) =>
    props.index &&
    `
    animation-delay: ${props.index * 0.04}s;
  `}
`;

const TableCell = styled.td`
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const SurveyInfo = styled.div``;

const SurveyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const SurveySubtitle = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 2px;
`;

const SurveyType = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;

  ${(props) => {
    switch (props.status) {
      case "active":
        return `
          background: #e8f5e8;
          color: #2e7d2e;
        `;
      case "scheduled":
        return `
          background: #fff4e6;
          color: #cc8500;
        `;
      case "draft":
        return `
          background: #f0f4ff;
          color: #476dfa;
        `;
      case "completed":
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      case "demo":
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      default:
        return `
          background: #f0f0f0;
          color: #666;
        `;
    }
  }}
`;

const DateRange = styled.div``;

const DateText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const ProgressInfo = styled.div``;

const ProgressText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 6px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #476dfa;
  border-radius: 3px;
  width: ${props => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease;
`;

const CompletionInfo = styled.div``;

const CompletionRate = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #2e7d2e;
  margin-bottom: 2px;
`;

const CompletionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0 0 20px 0;
  max-width: 350px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const ModalContent = styled.div`
  padding: 20px 24px 24px 24px;
`;

const ModalSubtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0 0 24px 0;
  text-align: center;
`;

const SurveyTypeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SurveyTypeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 4px 12px rgba(71, 109, 250, 0.1);
    transform: translateY(-1px);
  }
`;

const SurveyTypeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: ${props => {
    if (props.primary) return '#f0f4ff';
    if (props.secondary) return '#fff4e6';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.primary) return '#476dfa';
    if (props.secondary) return '#ff8c00';
    return '#7e7e7e';
  }};
  flex-shrink: 0;
`;

const SurveyTypeContent = styled.div`
  flex: 1;
`;

const SurveyTypeTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const SurveyTypeDescription = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
  line-height: 1.4;
`;

const SurveyTypeArrow = styled.div`
  color: #7e7e7e;
  opacity: 0.6;
  transition: all 0.2s;

  ${SurveyTypeOption}:hover & {
    color: #476dfa;
    opacity: 1;
    transform: translateX(2px);
  }
`;

// Second-step styles modeled after CreateSurveyModal.js
const SecondStepOptionsContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
`;

const SecondStepOptionCard = styled.div`
  flex: 1;
  border: 2px solid #e5e7eb; /* neutral[200] */
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #c7d2fe; /* primary[100]-ish */
    background-color: #eef2ff; /* primary[50]-ish */
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SecondStepIconContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background-color: #eef2ff; /* primary[50]-ish */
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #4f46e5; /* primary[600]-ish */
`;

const SecondStepOptionTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #1f2937; /* text.dark-ish */
  margin: 0 0 8px;
`;

const SecondStepOptionDescription = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #6b7280; /* text.gray-ish */
  margin: 0;
  line-height: 1.5;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  background: #fff;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  min-width: 120px;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;
